import React, { useRef, useState } from "react";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import {
  Dialog,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { FileCopyOutlined as FileCopyIcon } from "@mui/icons-material";
import * as Styled from "./styles";
import { copyToClipboard } from "utils/formatByText";
import { toast } from "utils/toast";
import CloseIcon from "@mui/icons-material/Close";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import {
  isDealerStoreType,
  isDriverStoreType,
} from "types/coverageAreaType";

interface CoverageAreaQRCodeProps {
  coverageAreaURL: string;
  coverageAreaName: string;
  currentCoverageArea?: CoverageAreaProps;
}

const CoverageAreaQRCode = ({
  coverageAreaURL,
  coverageAreaName,
  currentCoverageArea,
}: CoverageAreaQRCodeProps) => {
  const QRCodeRef = useRef(null);

  const [openModal, setOpenModal] = useState(false);
  const [selectedSize, setSelectedSize] = useState<
    "small" | "medium" | "large" | any
  >();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSize(event.target.value);
  };

  const handleDownloadQRCode = (size: "small" | "medium" | "large") => {
    const sizes = {
      small: 2.65,
      medium: 3.93,
      large: 44.29,
    };

    const scale = sizes[size];

    html2canvas(QRCodeRef.current!, { scale }).then((canvas) => {
      const QRCodeUri = canvas.toDataURL();

      const link = document.createElement("a");
      link.download = `QRCode-${coverageAreaName}-${size}.png`;
      link.href = QRCodeUri;
      link.click();
    });
  };
  const handleCopyToClipboard = () => {
    const copied = copyToClipboard(coverageAreaURL);

    if (copied) {
      toast("Link copiado!", {
        type: "success",
        autoClose: 500,
      });
    } else {
      toast("Falha ao copiar link!", {
        type: "error",
        autoClose: 500,
      });
    }
  };

  const isPortaria = isDealerStoreType(currentCoverageArea?.type);
  const isPortariaWithDriver = isDriverStoreType(currentCoverageArea?.type);

  const shouldShowQRCode =
    isPortaria || (isPortariaWithDriver && currentCoverageArea?.hasDriver);

  return (
    <Styled.Container>
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        style={{
          zIndex: 1500,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <DialogTitle>
          Baixar Código
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenModal(false);
            }}
            style={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#000",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <div style={{ padding: "20px", height: "300px", width: "550px" }}>
          Escolha o tamanho que deseja baixar o QR Code
          <RadioGroup
            aria-label="size"
            name="size"
            value={selectedSize}
            onChange={handleChange}
            style={{ flexDirection: "column", paddingTop: "40px" }}
          >
            <FormControlLabel
              value="small"
              control={<Radio />}
              label="Pequeno (Ideal para crachás)"
            />
            <FormControlLabel
              value="medium"
              control={<Radio />}
              label="Médio (Ideal para papel A4)"
            />
            <FormControlLabel
              value="large"
              control={<Radio />}
              label="Grande (Ideal para banner)"
            />
          </RadioGroup>
        </div>

        <Styled.ButtonModal
          onClick={() => {
            handleDownloadQRCode(selectedSize);
            setOpenModal(false);
            setSelectedSize("");
          }}
          disabled={!selectedSize}
          color="primary"
          variant="contained"
          size="large"
        >
          download
        </Styled.ButtonModal>
      </Dialog>
      <Typography variant="caption">
        Esta Área de Cobertura não possui mapa. Utilize o código QR para
        acessar.
      </Typography>
      <Styled.QRCodeContainer ref={QRCodeRef}>
        <QRCode
          size={256}
          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
          value={coverageAreaURL}
          viewBox="0 0 256 256"
          fgColor={shouldShowQRCode ? "#000" : "#E5E5E5"}
        />
      </Styled.QRCodeContainer>
      <Styled.DownloadQRCodeTouchable
        onClick={() => (shouldShowQRCode ? setOpenModal(true) : null)}
      >
        <Typography variant="caption">
          {shouldShowQRCode ? "Baixar código" : "QRCode desativado"}
        </Typography>
        <Styled.CloudDownloadIcon />
      </Styled.DownloadQRCodeTouchable>

      {!currentCoverageArea?.hasDriver && isPortariaWithDriver && (
        <Typography variant="caption" textAlign="center">
          Selecione um entregador para reativar o QRCode.
        </Typography>
      )}

      {(isPortaria ||
        (isPortariaWithDriver && currentCoverageArea?.hasDriver)) && (
        <Styled.CopyLinkContainer onClick={handleCopyToClipboard}>
          <Typography variant="caption">
            Link Direto: {coverageAreaURL}
          </Typography>
          <FileCopyIcon fontSize="small" />
        </Styled.CopyLinkContainer>
      )}
    </Styled.Container>
  );
};

export default CoverageAreaQRCode;
